import isEmpty from "lodash/isEmpty";
import { defineStore } from "pinia";

import { stripHTMLTagsFromString } from "@/helpers/common";
import { ID_NEW } from "@/helpers/constants/consentTemplates";
import {
  type ConsentTemplate,
  type ConsentTemplateErrors,
  type ConsentTypes,
  type PayloadConsentTemplate,
  type SimpleConsentTemplate,
} from "@/models/consentTemplate";
import consentTemplateService, {
  type GetConsentTemplatesParams,
} from "@/services/modules/consentTemplate";

import type {
  IPaginatedResponse,
  IResponseLinks,
  IResponseMeta,
} from "@/models/common";
import type { ConsentOptions } from "@/models/options";

interface ConsentTemplateState {
  templates: IPaginatedResponse<SimpleConsentTemplate>;
  activeTemplate: ConsentTemplate | null;
  activeTemplateErrors: ConsentTemplateErrors | null;
  consentOptions: ConsentOptions | null;
}

const useConsentTemplateStore = defineStore("consentTemplates", {
  state: (): ConsentTemplateState => ({
    templates: {
      data: [],
      meta: {} as IResponseMeta,
      links: {} as IResponseLinks,
    },
    activeTemplate: null,
    activeTemplateErrors: null,
    consentOptions: null,
  }),
  getters: {
    consentTypes(): ConsentTypes | null {
      return this.consentOptions?.definition.consent_types || null;
    },
    latestSchemaVersion(): ConsentOptions["latest_schema_version"] {
      return this.consentOptions?.latest_schema_version || "1.0.0";
    },
  },
  actions: {
    async getConsentOptions() {
      this.consentOptions = await consentTemplateService.getConsentOptions();
    },

    async getTemplates(params?: GetConsentTemplatesParams) {
      this.templates = await consentTemplateService.getAllTemplates(params);
    },

    async getActiveTemplate(id: ConsentTemplate["id"]) {
      this.activeTemplate = await consentTemplateService.getTemplate(id);
    },

    validateActiveTemplate() {
      const name = this.activeTemplate?.name
        ? undefined
        : "COMMON.FIELD_NAME_REQUIRED";
      const contentMessage = stripHTMLTagsFromString(
        this.activeTemplate?.content?.message ?? ""
      )
        ? undefined
        : "COMMON.MESSAGE_IS_REQUIRED";
      let consentMessages =
        this.activeTemplate?.content?.consents.map((consent) => {
          const type = consent.type ? undefined : "COMMON.TYPE_IS_REQUIRED";
          const message = stripHTMLTagsFromString(consent.message ?? "")
            ? undefined
            : "COMMON.MESSAGE_IS_REQUIRED";
          if (!type && !message) {
            return null;
          }
          return {
            type,
            message,
          };
        }) ?? [];

      if (!consentMessages.some((message) => !!message)) {
        consentMessages = [];
      }

      const consentRequired = this.activeTemplate?.content?.consents.some(
        (consent) => consent.is_required
      )
        ? undefined
        : "CUSTOMIZE.CONSENT.AT_LEAST_ONE_CONSENT_REQUIRED";

      this.activeTemplateErrors = {
        name,
        contentMessage,
        consentMessages,
        consentRequired,
        validated: true,
      };

      return Object.values(this.activeTemplateErrors).every(isEmpty);
    },

    async saveActiveTemplate(
      template: ConsentTemplate,
      options = { force_default: false }
    ) {
      const method =
        template.id === ID_NEW
          ? consentTemplateService.createTemplate
          : consentTemplateService.updateTemplate;
      const payload: PayloadConsentTemplate = {
        content: template.content,
        is_default: options.force_default || template.is_default,
        force_default: options.force_default,
        name: template.name,
      };
      this.activeTemplate = await method(payload, template.id);
    },
  },
});

export default useConsentTemplateStore;
